import React, { useState, useEffect, forwardRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, setDoc, collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from '../hooks/useAuthState';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';

// Define the API URL based on the environment.
// This should point to your deployed Cloudflare Worker.
const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8787'
  : 'https://beta-events-payment-gateway.youthxconnect.workers.dev';

function RegisterEvent() {
  const { eventId } = useParams();
  const [user, loading] = useAuthState(auth);
  const [event, setEvent] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAuthComplete, setIsAuthComplete] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [existingRegistration, setExistingRegistration] = useState(null);
  const [canProceed, setCanProceed] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  useEffect(() => {
    if (!loading && user) {
      setCurrentStep(2); // Skip auth step if user is logged in
    }
  }, [user, loading]);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const eventRef = doc(db, 'events', eventId);
        const eventSnap = await getDoc(eventRef);
        if (eventSnap.exists()) {
          setEvent({ id: eventSnap.id, ...eventSnap.data() });
        }
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  useEffect(() => {
    if (user) {
      setIsAuthComplete(true);
    } else {
      setIsAuthComplete(false);
    }
  }, [user]);

  useEffect(() => {
    /**
     * Comprehensive existing registration check
     * 
     * This function safely checks for existing registrations with enhanced error handling:
     * - Handles multiple registrations for the same user/event
     * - Prioritizes the most recent registration
     * - Provides detailed logging for debugging
     * - Gracefully handles network errors and edge cases
     */
    const checkExistingRegistration = async () => {
      if (!user || !eventId) {
        console.log('👤 Skipping registration check - user or eventId not available');
        return;
      }

      try {
        console.log('🔍 Checking for existing registrations...', {
          userId: user.uid,
          eventId,
          timestamp: new Date().toISOString()
        });

        const registrationsRef = collection(db, `registrations/${eventId}/registrations`);
        const q = query(
          registrationsRef,
          where('userId', '==', user.uid)
        );
        
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          console.log('✅ No existing registrations found - user can proceed');
          setExistingRegistration(null);
          return;
        }

        // Handle multiple registrations (edge case)
        const registrations = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          registrations.push({
            id: doc.id,
            ...data,
            // Ensure we have a timestamp for sorting
            sortTimestamp: data.updatedAt || data.createdAt || data.timestamp || new Date(0).toISOString()
          });
        });

        console.log(`📋 Found ${registrations.length} existing registration(s):`, 
          registrations.map(r => ({ id: r.id, status: r.status, created: r.createdAt }))
        );

        // Sort by most recent first
        registrations.sort((a, b) => new Date(b.sortTimestamp) - new Date(a.sortTimestamp));

        // Determine which registration to show
        let registrationToShow = null;

        // Priority order: success > pending > failed > draft > others
        const priorityOrder = ['success', 'pending', 'failed', 'draft'];
        
        for (const status of priorityOrder) {
          const matchingReg = registrations.find(r => r.status === status);
          if (matchingReg) {
            registrationToShow = matchingReg;
            break;
          }
        }

        // If no priority status found, use the most recent one
        if (!registrationToShow) {
          registrationToShow = registrations[0];
        }

        console.log('📌 Selected registration to display:', {
          id: registrationToShow.id,
          status: registrationToShow.status,
          created: registrationToShow.createdAt,
          isLatest: registrationToShow.id === registrations[0].id
        });

        // Clean up the registration data
        const cleanedRegistration = {
          id: registrationToShow.id,
          status: registrationToShow.status || 'unknown',
          createdAt: registrationToShow.createdAt,
          updatedAt: registrationToShow.updatedAt,
          eventId: registrationToShow.eventId,
          userId: registrationToShow.userId,
          // Include other relevant fields without sensitive data
          personalInfo: registrationToShow.personalInfo,
          paymentDetails: registrationToShow.paymentDetails ? {
            ...registrationToShow.paymentDetails,
            // Remove sensitive payment info
            cardNumber: null,
            bankDetails: null
          } : null
        };

        setExistingRegistration(cleanedRegistration);

        // Log warning if multiple registrations exist
        if (registrations.length > 1) {
          console.warn('⚠️ Multiple registrations found for user. This may indicate a data issue:', {
            count: registrations.length,
            statuses: registrations.map(r => r.status),
            selected: registrationToShow.status
          });
        }

      } catch (error) {
        console.error('❌ Error checking existing registration:', {
          error: error.message,
          stack: error.stack,
          userId: user?.uid,
          eventId,
          timestamp: new Date().toISOString()
        });

        // Don't block the user if registration check fails
        // They can still attempt to register and the backend will handle duplicates
        setExistingRegistration(null);

        // Show user-friendly error if it's a network issue
        if (error.message.includes('network') || error.message.includes('fetch')) {
          console.warn('🌐 Network error during registration check - allowing user to proceed');
        }
      }
    };

    checkExistingRegistration();
  }, [user, eventId]);

  /**
   * Handles deletion of existing registrations with comprehensive error handling
   * 
   * This function safely removes existing registrations that are not in 'success' status,
   * allowing users to start fresh registrations. It includes:
   * - Multi-status support (failed, pending, draft, expired)
   * - Comprehensive validation and authorization checks
   * - User-friendly error messages and confirmation dialogs
   * - Atomic operation with rollback capability
   * - Professional logging for debugging
   * 
   * @returns {Promise<void>}
   */
  const handleDeleteRegistration = async () => {
    try {
      // === PHASE 1: USER CONFIRMATION ===
      const confirmationMessage = existingRegistration?.status === 'failed' 
        ? 'Your previous registration failed. Would you like to remove it and start a new registration?'
        : 'Are you sure you want to delete your current registration and start over?';
      
      if (!window.confirm(confirmationMessage)) {
        return;
      }

      // Immediately show loading state for better UX
      setIsSubmitting(true);

      // === PHASE 2: COMPREHENSIVE REGISTRATION LOOKUP ===
      // Get all non-success registrations for this user/event combination
      const registrationsRef = collection(db, `registrations/${eventId}/registrations`);
      
      // Define deletable statuses - everything except 'success'
      const deletableStatuses = ['pending', 'draft', 'failed', 'expired', 'cancelled', 'error'];
      
      const q = query(
        registrationsRef,
        where('userId', '==', user.uid),
        where('status', 'in', deletableStatuses)
      );
      
      console.log('🔍 Searching for deletable registrations...', {
        eventId,
        userId: user.uid,
        allowedStatuses: deletableStatuses,
        existingRegistrationStatus: existingRegistration?.status
      });
      
      const querySnapshot = await getDocs(q);
      
      // === PHASE 3: VALIDATION AND SAFETY CHECKS ===
      if (querySnapshot.empty) {
        // Fallback: Try to find the specific registration we know exists
        console.warn('⚠️ No registrations found with status filter, attempting direct lookup...');
        
        if (existingRegistration?.id) {
          try {
            const directDocRef = doc(db, `registrations/${eventId}/registrations`, existingRegistration.id);
            const directDoc = await getDoc(directDocRef);
            
            if (directDoc.exists()) {
              const directData = directDoc.data();
              
              // Check if it's actually deletable
              if (directData.status === 'success') {
                throw new Error('Cannot delete a successful registration. Please contact support if you need assistance.');
              }
              
              // Proceed with direct deletion
              await deleteDoc(directDocRef);
              console.log('✅ Direct deletion successful');
              
              // Reset states
              setExistingRegistration(null);
              setAnswers({});
              setCurrentStep(user ? 2 : 1);
              
              alert(`Previous ${directData.status} registration has been removed. You can now start a new registration.`);
              return;
              
            } else {
              throw new Error('Registration not found in database. It may have been already deleted.');
            }
          } catch (directError) {
            console.error('❌ Direct deletion failed:', directError);
            throw new Error(`Unable to delete registration: ${directError.message}`);
          }
        } else {
          throw new Error('No registration found to delete. Please refresh the page and try again.');
        }
      }

      // === PHASE 4: PROCESS FOUND REGISTRATIONS ===
      const registrationsToDelete = [];
      
      querySnapshot.forEach((docSnapshot) => {
        const registrationData = docSnapshot.data();
        
        // Validate ownership
        if (registrationData.userId !== user.uid) {
          console.error('❌ Authorization error: Registration belongs to different user', {
            registrationUserId: registrationData.userId,
            currentUserId: user.uid,
            registrationId: docSnapshot.id
          });
          return; // Skip this registration
        }
        
        // Log registration details for debugging
        console.log('📋 Found deletable registration:', {
          id: docSnapshot.id,
          status: registrationData.status,
          createdAt: registrationData.createdAt,
          userId: registrationData.userId
        });
        
        registrationsToDelete.push({
          ref: docSnapshot.ref,
          data: registrationData,
          id: docSnapshot.id
        });
      });

      if (registrationsToDelete.length === 0) {
        throw new Error('No valid registrations found to delete after authorization check.');
      }

      // === PHASE 5: ATOMIC DELETION PROCESS ===
      console.log(`🗑️ Attempting to delete ${registrationsToDelete.length} registration(s)...`);
      
      const deletionPromises = registrationsToDelete.map(async (regToDelete) => {
        try {
          await deleteDoc(regToDelete.ref);
          console.log(`✅ Successfully deleted registration: ${regToDelete.id}`);
          return { success: true, id: regToDelete.id, status: regToDelete.data.status };
        } catch (deleteError) {
          console.error(`❌ Failed to delete registration ${regToDelete.id}:`, deleteError);
          return { success: false, id: regToDelete.id, error: deleteError.message };
        }
      });

      // Wait for all deletions to complete
      const deletionResults = await Promise.allSettled(deletionPromises);
      
      // Process results
      const successfulDeletions = [];
      const failedDeletions = [];
      
      deletionResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          if (result.value.success) {
            successfulDeletions.push(result.value);
          } else {
            failedDeletions.push(result.value);
          }
        } else {
          failedDeletions.push({
            success: false,
            id: registrationsToDelete[index]?.id || 'unknown',
            error: result.reason?.message || 'Unknown error'
          });
        }
      });

      // === PHASE 6: RESULT HANDLING AND USER FEEDBACK ===
      if (successfulDeletions.length > 0) {
        console.log('✅ Deletion Summary:', {
          successful: successfulDeletions.length,
          failed: failedDeletions.length,
          details: successfulDeletions
        });

        // Reset application state
        setExistingRegistration(null);
        setAnswers({});
        setCurrentStep(user ? 2 : 1);
        
        // Provide user feedback
        const statusList = [...new Set(successfulDeletions.map(d => d.status))].join(', ');
        const message = successfulDeletions.length === 1
          ? `Previous ${statusList} registration has been removed. You can now start a new registration.`
          : `${successfulDeletions.length} previous registrations have been removed. You can now start a new registration.`;
          
        alert(message);
        
        // Log success for monitoring
        console.log('🎉 Registration deletion completed successfully');
        
      } else {
        // All deletions failed
        const errorMessages = failedDeletions.map(f => f.error).join('; ');
        throw new Error(`Failed to delete registrations: ${errorMessages}`);
      }

      // === PHASE 7: PARTIAL SUCCESS HANDLING ===
      if (failedDeletions.length > 0) {
        console.warn('⚠️ Some deletions failed:', failedDeletions);
        
        // If we had some successes, warn about partial failures
        const warningMessage = `Registration deletion completed with ${failedDeletions.length} warnings. ` +
          `You can proceed with new registration. If you encounter issues, please refresh the page.`;
        
        setTimeout(() => alert(warningMessage), 100); // Delay to avoid multiple alerts
      }

    } catch (error) {
      // === COMPREHENSIVE ERROR HANDLING ===
      console.error('❌ Registration deletion failed:', {
        error: error.message,
        stack: error.stack,
        eventId,
        userId: user?.uid,
        existingRegistrationId: existingRegistration?.id,
        existingRegistrationStatus: existingRegistration?.status,
        timestamp: new Date().toISOString()
      });

      // Provide user-friendly error messages based on error type
      let userMessage = 'Failed to delete registration. ';
      
      if (error.message.includes('not found')) {
        userMessage += 'The registration may have been already removed. Please refresh the page.';
      } else if (error.message.includes('permission') || error.message.includes('authorization')) {
        userMessage += 'You do not have permission to delete this registration.';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        userMessage += 'Network error occurred. Please check your connection and try again.';
      } else {
        userMessage += `${error.message}`;
      }

      alert(userMessage);
      
      // Optional: Provide recovery suggestions
      if (error.message.includes('refresh')) {
        if (window.confirm('Would you like to refresh the page to resolve this issue?')) {
          window.location.reload();
        }
      }

    } finally {
      // Always reset loading state
      setIsSubmitting(false);
    }
  };

  if (isPageLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <h2 className="text-2xl font-semibold text-gray-800">Event not found</h2>
        <button onClick={() => window.history.back()} className="mt-4 text-indigo-600 hover:underline">
          Go Back
        </button>
      </div>
    );
  }

  if (existingRegistration) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-8 bg-white shadow-xl rounded-lg mt-14">
        <div className="flex items-start space-x-4 mb-6">
          {/* Status Icon */}
          <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold
            ${existingRegistration.status === 'success' ? 'bg-green-500' : 
              existingRegistration.status === 'failed' ? 'bg-red-500' : 
              existingRegistration.status === 'pending' ? 'bg-yellow-500' : 'bg-gray-500'}`}>
            {existingRegistration.status === 'success' ? '✓' : 
             existingRegistration.status === 'failed' ? '✗' : 
             existingRegistration.status === 'pending' ? '⏳' : '?'}
          </div>
          
          {/* Main Content */}
          <div className="flex-1">
            <h2 className="text-2xl font-semibold mb-2 text-gray-800">
              {existingRegistration.status === 'success' ? 'Registration Complete' : 'Previous Registration Found'}
            </h2>
            
            {/* Status-specific messages */}
            {existingRegistration.status === 'success' && (
              <div className="space-y-2">
                <p className="text-green-700 font-medium">
                  ✅ You have successfully registered for this event.
                </p>
                <p className="text-gray-600">
                  Your registration is confirmed and you're all set to attend. 
                  Check your email for confirmation details.
                </p>
              </div>
            )}
            
            {existingRegistration.status === 'failed' && (
              <div className="space-y-2">
                <p className="text-red-700 font-medium">
                  ❌ Your previous registration failed.
                </p>
                <p className="text-gray-600">
                  The payment or registration process wasn't completed successfully. 
                  You can remove this registration and start fresh.
                </p>
              </div>
            )}
            
            {existingRegistration.status === 'pending' && (
              <div className="space-y-2">
                <p className="text-yellow-700 font-medium">
                  ⏳ Your registration is being processed.
                </p>
                <p className="text-gray-600">
                  Your registration is currently being reviewed or payment is pending. 
                  If this has been pending for a while, you may start a new registration.
                </p>
              </div>
            )}
            
            {existingRegistration.status === 'draft' && (
              <div className="space-y-2">
                <p className="text-gray-700 font-medium">
                  📝 You have an incomplete registration.
                </p>
                <p className="text-gray-600">
                  Your registration was started but not completed. 
                  You can remove this draft and start a new registration.
                </p>
              </div>
            )}
            
            {!['success', 'failed', 'pending', 'draft'].includes(existingRegistration.status) && (
              <div className="space-y-2">
                <p className="text-gray-700 font-medium">
                  📋 Registration status: {existingRegistration.status}
                </p>
                <p className="text-gray-600">
                  Your registration is in '{existingRegistration.status}' status. 
                  You can remove this registration and start a new one if needed.
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Registration Details */}
        {existingRegistration.id && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Registration Details</h3>
            <div className="space-y-1 text-sm text-gray-600">
              <p><span className="font-medium">ID:</span> {existingRegistration.id}</p>
              {existingRegistration.createdAt && (
                <p><span className="font-medium">Created:</span> {new Date(existingRegistration.createdAt).toLocaleString()}</p>
              )}
              {existingRegistration.updatedAt && existingRegistration.updatedAt !== existingRegistration.createdAt && (
                <p><span className="font-medium">Updated:</span> {new Date(existingRegistration.updatedAt).toLocaleString()}</p>
              )}
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          {existingRegistration.status === 'success' ? (
            // Success state - show contact support option
            <div className="flex flex-col sm:flex-row gap-3">
              <a 
                href={`mailto:events@youthxconnect.com?subject=Registration Query - ${event?.title || 'Event'}&body=Hi, I have a question about my registration (ID: ${existingRegistration.id}).`}
                className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 001.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Contact Support
              </a>
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Go Back
              </button>
            </div>
          ) : (
            // Non-success states - show delete and start new option
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleDeleteRegistration}
                disabled={isSubmitting}
                className={`flex items-center justify-center px-6 py-3 rounded-lg text-white font-medium transition-all
                  ${isSubmitting 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95'
                  }`}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    {existingRegistration.status === 'failed' ? 'Remove and Start New' : 'Delete and Start Over'}
                  </>
                )}
              </button>
              
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        
        {/* Help Text */}
        {existingRegistration.status !== 'success' && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              <span className="font-medium">💡 Tip:</span> If you're experiencing issues or need help, 
              contact us at <a href="mailto:events@youthxconnect.com" className="underline">events@youthxconnect.com</a>
            </p>
          </div>
        )}
      </div>
    );
  }

  const handleNext = () => {
    setCurrentStep(prev => prev + 1);
  };

  const handlePrev = () => {
    setCurrentStep(prev => prev - 1);
  };

  /**
   * Handles payment submission with optimized performance and professional error handling
   * 
   * Key optimizations:
   * - Pre-validation to fail fast on invalid data
   * - Optimistic UI updates for better UX
   * - Non-blocking error tracking
   * - Progressive enhancement for payment gateway integration
   * 
   * @param {string|null} couponCode - Applied coupon code if any
   * @param {number} amount - Final payment amount after discounts
   */
  const handleSubmit = async (couponCode, amount) => {
    // Immediately set submitting state for instant UI feedback
    setIsSubmitting(true);
  
    try {
      // === PHASE 1: IMMEDIATE VALIDATION (FAIL FAST) ===
      // Validate user data locally before making any API calls
      if (!userData?.name || !userData?.email || !userData?.phone) {
        throw new Error('Missing user information. Please complete your profile.');
      }

      // Validate event data
      if (!event?.id || !eventId) {
        throw new Error('Invalid event. Please refresh and try again.');
      }

      // Validate user authentication
      if (!user?.uid) {
        throw new Error('Authentication required. Please log in.');
      }

      // === PHASE 2: DATA PREPARATION ===
      // Prepare optimized registration payload with minimal data
      const registrationPayload = {
        // Core identifiers
        eventId,
        userId: user.uid,
        
        // Personal information (validated above)
        firstname: userData.name.split(' ')[0],
        lastname: userData.name.split(' ').slice(1).join(' ') || '',
        email: userData.email,
        phone: userData.phone,
        
        // Event responses
        answers,
        
        // Payment details
        amount: parseFloat(amount).toFixed(2),
        couponCode: couponCode || null,
        
        // Optimization: Include event type for faster backend processing
        isFreeEvent: !event?.price || parseFloat(event.price) === 0,
        
        // Client metadata for debugging (minimal for performance)
        clientMetadata: {
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent.substring(0, 100), // Truncate for efficiency
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        }
      };

      // === PHASE 3: API COMMUNICATION WITH TIMEOUT ===
      const API_TIMEOUT = 15000; // 15 seconds timeout for better UX
      const apiUrl = `${API_BASE_URL}/api/register`;
      
      // Create abort controller for request timeout
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), API_TIMEOUT);
      
      console.log("🚀 Initiating registration:", {
        eventId: registrationPayload.eventId,
        userId: registrationPayload.userId,
        email: registrationPayload.email,
        amount: registrationPayload.amount,
        isFreeEvent: registrationPayload.isFreeEvent
      });

      try {
        // Make API request with timeout and optimized headers
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Add cache control for better performance
            'Cache-Control': 'no-cache',
            // Add request ID for tracking
            'X-Request-ID': `reg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          },
          body: JSON.stringify(registrationPayload),
          signal: abortController.signal,
        });

        // Clear timeout on successful response
        clearTimeout(timeoutId);

        // === PHASE 4: RESPONSE HANDLING ===
        if (!response.ok) {
          // Handle different error types professionally
          let errorMessage = 'Registration failed. Please try again.';
          
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
          } catch {
            // If JSON parsing fails, use status text
            errorMessage = `Server error (${response.status}): ${response.statusText}`;
          }
          
          throw new Error(errorMessage);
        }

        // === PHASE 5: SUCCESS HANDLING ===
        if (registrationPayload.isFreeEvent) {
          // Handle free event registration with JSON response
          const data = await response.json();
          
          if (!data.success || !data.redirectUrl) {
            throw new Error('Invalid response from server.');
          }

          console.log("✅ Free registration successful, redirecting to:", data.redirectUrl);
          
          // Use window.location.href for reliable redirect
          window.location.href = data.redirectUrl;
          return;
        }

        // === PHASE 6: PAID EVENT PAYMENT GATEWAY INTEGRATION ===
        // For paid events, handle PayU form with performance optimization
        const htmlContent = await response.text();
        
        if (!htmlContent || htmlContent.length < 100) {
          throw new Error('Invalid payment form received from server.');
        }

        console.log("💳 PayU form received, initializing payment gateway...");
        
        // Professional payment form handling with better UX
        // Create a new document context for cleaner form submission
        const paymentWindow = window.open('', '_self');
        if (!paymentWindow) {
          throw new Error('Popup blocker detected. Please allow popups and try again.');
        }

        // Write the PayU form with enhanced error handling
        try {
          paymentWindow.document.open();
          paymentWindow.document.write(htmlContent);
          paymentWindow.document.close();
        } catch (writeError) {
          console.error('Error writing payment form:', writeError);
          // Fallback: Use document.write on current window
          document.open();
          document.write(htmlContent);
          document.close();
        }

      } catch (fetchError) {
        // Clear timeout in case of fetch error
        clearTimeout(timeoutId);
        
        // Handle specific fetch errors professionally
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timed out. Please check your connection and try again.');
        } else if (fetchError.message.includes('NetworkError')) {
          throw new Error('Network error. Please check your internet connection.');
        } else {
          throw fetchError;
        }
      }

    } catch (error) {
      // === COMPREHENSIVE ERROR HANDLING ===
      console.error('❌ Registration error:', {
        message: error.message,
        stack: error.stack,
        eventId,
        userId: user?.uid,
        timestamp: new Date().toISOString()
      });

      // Show user-friendly error message
      const userMessage = error.message || 'Registration failed. Please try again.';
      
      // Use modern notification instead of alert for better UX
      if (window.Notification && Notification.permission === 'granted') {
        new Notification('Registration Error', {
          body: userMessage,
          icon: '/favicon.ico'
        });
      } else {
        alert(userMessage);
      }

      // Optional: Track error for analytics (non-blocking)
      try {
        // Send error to analytics service (implementation depends on your analytics setup)
        console.warn('Error tracking not implemented');
      } catch (trackingError) {
        // Ignore tracking errors silently
        console.debug('Error tracking failed:', trackingError);
      }

    } finally {
      // Always reset submitting state
      setIsSubmitting(false);
    }
  };
  

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 bg-white shadow-xl rounded-lg mt-14">
      <h2 className="text-3xl font-semibold mb-6 text-gray-800">{`Register for ${event?.title || 'Event'}`}</h2>
      <div className="mb-8">
        <ProgressBar currentStep={currentStep} steps={[
          { title: 'Account', icon: '👤' },
          { title: 'Details', icon: '📝' },
          { title: 'Questions', icon: '❓' },
          { title: 'Payment', icon: '💳' }
        ]} />
      </div>
      
      {/* Remove the conditional Transition wrapper */}
      <div className="min-h-[400px] transition-all duration-300 ease-in-out"> {/* Fixed height container */}
        {currentStep === 1 && !user && (
          <AuthStep />
        )}
        {currentStep === 2 && user && (
          <AccountCreation user={user} setUserData={setUserData} />
        )}
        {currentStep === 3 && user && (
          <Questionnaire 
            event={event} 
            answers={answers} 
            setAnswers={setAnswers}
            setCanProceed={setCanProceed}
          />
        )}
        {currentStep === 4 && user && (
          <PaymentSummary 
            event={event} 
            answers={answers} 
            handleSubmit={handleSubmit} 
            isSubmitting={isSubmitting} 
            userData={userData}
            appliedCoupon={appliedCoupon}
            setAppliedCoupon={setAppliedCoupon}
            handlePrev={handlePrev}
          />
        )}
      </div>

      <div className="flex justify-between mt-8 pt-6 border-t">
        {currentStep > (user ? 2 : 1) && (
          <button
            onClick={handlePrev}
            className="bg-gray-200 text-gray-800 px-5 py-2 rounded-lg shadow hover:bg-gray-300 transition"
          >
            Previous
          </button>
        )}
        {((currentStep < 4 && user) || (!user && currentStep === 1)) && (
          <button
            onClick={handleNext}
            disabled={(currentStep === 1 && !isAuthComplete) || (currentStep === 3 && !canProceed)}
            className={`${
              (currentStep === 1 && !isAuthComplete) || (currentStep === 3 && !canProceed)
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-indigo-600 hover:bg-indigo-700'
            } text-white px-5 py-2 rounded-lg shadow transition ml-auto`}
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
}

// Constants for storage keys
const STORAGE_KEYS = {
  USER_DATA: 'yxc_user_data',
  AUTH_SESSION: 'yxc_auth_session',
  REGISTRATION_DRAFT: 'yxc_registration_draft'
};

// Utility functions for localStorage handling with error handling
const storageUtils = {
  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Storage error:', error);
    }
  },
  get: (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error('Storage retrieval error:', error);
      return null;
    }
  },
  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Storage removal error:', error);
    }
  }
};

// New AuthStep component
const AuthStep = forwardRef((props, ref) => {
  const [isLogin, setIsLogin] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});

  // Initialize formData first
  const [formData, setFormData] = useState(() => {
    try {
      const savedData = storageUtils.get(STORAGE_KEYS.USER_DATA);
      return savedData || {
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
        name: '',
      };
    } catch (error) {
      console.error('Error loading saved data:', error);
      return {
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
        name: '',
      };
    }
  });

  // Move validation function after formData initialization
  const validateForm = useCallback(() => {
    const errors = {};
    
    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters long';
    }
    
    if (!isLogin) {
      // Name validation
      if (!formData.name) {
        errors.name = 'Full name is required';
      } else if (!formData.name.trim().includes(' ')) {
        errors.name = 'Please enter your full name (first and last name)';
      }

      // Phone validation
      if (!formData.phone) {
        errors.phone = 'Phone number is required';
      } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
        errors.phone = 'Please enter a valid 10-digit Indian mobile number';
      }

      // Confirm password validation
      if (!formData.confirmPassword) {
        errors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, isLogin]);

  // Handle form input changes
  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      // Save non-sensitive data to storage
      if (['email', 'name', 'phone'].includes(field)) {
        try {
          storageUtils.set(STORAGE_KEYS.USER_DATA, {
            email: newData.email,
            name: newData.name,
            phone: newData.phone
          });
        } catch (error) {
          console.error('Error saving to storage:', error);
        }
      }
      return newData;
    });
  }, []);

  // Rest of the component logic
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting || isLoadingUser) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError('');
    setIsLoadingUser(true);

    try {
      if (isLogin) {
        await handleLogin();
      } else {
        await handleRegistration();
      }
    } catch (err) {
      handleAuthError(err);
    } finally {
      setIsSubmitting(false);
      setIsLoadingUser(false);
    }
  };

  // Separated login logic
  const handleLogin = async () => {
    const userCredential = await signInWithEmailAndPassword(
      auth, 
      formData.email, 
      formData.password
    );
    
    // Save session data with timestamp
    storageUtils.set(STORAGE_KEYS.AUTH_SESSION, {
      email: userCredential.user.email,
      lastLogin: new Date().toISOString(),
      userId: userCredential.user.uid
    });

    await fetchAndStoreUserData(userCredential.user.uid);
  };

  // Separated registration logic
  const handleRegistration = async () => {
    // Additional validation for registration
    if (formData.password.length < 8) {
      throw new Error('Password must be at least 8 characters long');
    }
    if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      throw new Error('Please enter a valid 10-digit Indian mobile number');
    }
    if (!formData.name.trim().includes(' ')) {
      throw new Error('Please enter your full name (first name and last name)');
    }

    const userCredential = await createUserWithEmailAndPassword(
      auth, 
      formData.email, 
      formData.password
    );
    
    const userData = createUserData(userCredential);
    await setDoc(doc(db, 'users', userCredential.user.uid), userData);
    
    // Save to local storage with error handling
    try {
      storageUtils.set(STORAGE_KEYS.USER_DATA, {
        email: userData.email,
        name: userData.name,
        phone: userData.phone
      });
    } catch (error) {
      console.error('Error saving user data to local storage:', error);
      // Continue execution even if local storage fails
    }
  };

  // Helper function to create user data object
  const createUserData = (userCredential) => ({
    email: formData.email,
    phone: formData.phone,
    name: formData.name,
    userType: 'User',
    createdAt: new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
    preferences: {
      notifications: true,
      newsletter: true
    },
    metadata: {
      registrationSource: 'web',
      userAgent: navigator.userAgent,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      platform: navigator.platform,
      language: navigator.language
    }
  });

  // Helper function to fetch and store user data
  const fetchAndStoreUserData = async (uid) => {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      storageUtils.set(STORAGE_KEYS.USER_DATA, {
        email: userData.email,
        name: userData.name,
        phone: userData.phone
      });
    }
  };

  // Enhanced error handling
  const handleAuthError = (err) => {
    console.error('Auth error:', err);
    const errorMessage = getAuthErrorMessage(err);
    setError(errorMessage);
  };

  // Helper function to get user-friendly error messages
  const getAuthErrorMessage = (err) => {
    const errorMessages = {
      'auth/email-already-in-use': 'This email is already registered. Please login instead.',
      'auth/invalid-email': 'Please enter a valid email address.',
      'auth/weak-password': 'Password must be at least 8 characters long.',
      'auth/wrong-password': 'Invalid email or password.',
      'auth/user-not-found': 'No account found with this email.',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.'
    };
    return errorMessages[err.code] || err.message;
  };

  return (
    <div className="animate-fadeIn">
      <h3 className="text-2xl font-semibold mb-4">
        {isLogin ? 'Login' : 'Create Account'}
      </h3>
      {error && <p className="text-red-500 mb-4">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-4">
        {!isLogin && (
          <div>
            <label className="block text-gray-700">Full Name</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`w-full border rounded px-3 py-2 ${
                formErrors.name ? 'border-red-500' : ''
              }`}
            />
            {formErrors.name && (
              <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
            )}
          </div>
        )}
        <div>
          <label className="block text-gray-700">Email</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`w-full border rounded px-3 py-2 ${
              formErrors.email ? 'border-red-500' : ''
            }`}
          />
          {formErrors.email && (
            <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
          )}
        </div>
        {!isLogin && (
          <div>
            <label className="block text-gray-700">Phone</label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className={`w-full border rounded px-3 py-2 ${
                formErrors.phone ? 'border-red-500' : ''
              }`}
            />
            {formErrors.phone && (
              <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>
            )}
          </div>
        )}
        <div>
          <label className="block text-gray-700">Password</label>
          <input
            type="password"
            required
            minLength={6}
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            className={`w-full border rounded px-3 py-2 ${
              formErrors.password ? 'border-red-500' : ''
            }`}
          />
          {formErrors.password && (
            <p className="text-red-500 text-sm mt-1">{formErrors.password}</p>
          )}
        </div>
        {!isLogin && (
          <div>
            <label className="block text-gray-700">Confirm Password</label>
            <input
              type="password"
              required
              minLength={6}
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className={`w-full border rounded px-3 py-2 ${
                formErrors.confirmPassword ? 'border-red-500' : ''
              }`}
            />
            {formErrors.confirmPassword && (
              <p className="text-red-500 text-sm mt-1">{formErrors.confirmPassword}</p>
            )}
          </div>
        )}
        <button type="submit" className="w-full bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700">
          {isLogin ? 'Login' : 'Register'}
        </button>
      </form>
      <div className="mt-4 text-center">
        <button 
          onClick={() => setIsLogin(!isLogin)} 
          className="text-indigo-600 hover:underline"
        >
          {isLogin ? "Don't have an account? Register" : "Already have an account? Login"}
        </button>
      </div>
    </div>
  );
});

// Update ProgressBar component
function ProgressBar({ currentStep, steps }) {
  return (
    <div className="relative">
      <div className="absolute top-1/2 w-full h-0.5 bg-gray-200 -translate-y-1/2"></div>
      <div className="relative flex justify-between">
        {steps.map((step, index) => (
          <div key={index} className="flex flex-col items-center">
            <div 
              className={`w-12 h-12 rounded-full flex items-center justify-center text-xl z-10 transition-all duration-300 
                ${currentStep > index 
                  ? 'bg-green-500 text-white' 
                  : currentStep === index + 1 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-gray-200 text-gray-600'}`}
            >
              {step.icon}
            </div>
            <span className={`mt-2 text-sm font-medium
              ${currentStep === index + 1 ? 'text-indigo-600' : 'text-gray-500'}`}>
              {step.title}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Forward refs to handle Transition requirements
const AccountCreation = forwardRef(({ user, setUserData: parentSetUserData }, ref) => {
  const [localUserData, setLocalUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAndSetUserData = async () => {
      try {
        // Try to get data from local storage first
        const cachedData = storageUtils.get(STORAGE_KEYS.USER_DATA);
        if (cachedData) {
          setLocalUserData(cachedData);
          parentSetUserData(cachedData);
        }

        // Fetch fresh data from Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const freshData = userDoc.data();
          setLocalUserData(freshData);
          parentSetUserData(freshData);
          
          // Update cache with fresh data
          storageUtils.set(STORAGE_KEYS.USER_DATA, {
            email: freshData.email,
            name: freshData.name,
            phone: freshData.phone
          });
        } else {
          throw new Error('User data not found');
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user data. Please refresh the page.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndSetUserData();
  }, [user, parentSetUserData]);

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 p-4 border border-red-300 rounded">
        {error}
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <h3 className="text-2xl font-semibold mb-4 text-gray-700">Account Details</h3>
      <div className="bg-white p-4 rounded-lg shadow-sm">
        <p className="text-lg text-gray-600 mb-2">
          <span className="font-medium">Name:</span> {localUserData?.name || 'Not provided'}
        </p>
        <p className="text-lg text-gray-600 mb-2">
          <span className="font-medium">Email:</span> {localUserData?.email || user.email}
        </p>
        <p className="text-lg text-gray-600">
          <span className="font-medium">Phone:</span> {localUserData?.phone || 'Not provided'}
        </p>
      </div>
      {(!localUserData?.name || !localUserData?.phone) && (
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-yellow-700">
            Please update your profile to ensure smooth registration process.
          </p>
        </div>
      )}
    </div>
  );
});

// Update Questionnaire component
const Questionnaire = forwardRef(({ event, answers, setAnswers, setCanProceed }, ref) => {
  const [errors, setErrors] = useState({});

  const validateAllAnswers = useCallback(() => {
    const newErrors = {};
    let isValid = true;

    event.questions.forEach((question, index) => {
      const answer = answers[`question${index + 1}`];
      if (!answer || (Array.isArray(answer) && answer.length === 0)) {
        newErrors[`question${index + 1}`] = 'This field is required';
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [answers, event.questions]);

  useEffect(() => {
    const isValid = validateAllAnswers();
    setCanProceed(isValid);
  }, [validateAllAnswers, setCanProceed]);

  const validateAnswer = (question, value) => {
    if (question.required && (!value || (Array.isArray(value) && value.length === 0))) {
      return 'This field is required';
    }
    return '';
  };

  const handleChange = (questionId, value, question) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    setErrors(prev => ({ ...prev, [questionId]: validateAnswer(question, value) }));
  };

  return (
    <div className="animate-fadeIn space-y-6">
      <h3 className="text-2xl font-semibold mb-6 text-gray-700">Event Questions</h3>
      {event.questions.map((question, index) => (
        <div key={index} className="p-4 bg-gray-50 rounded-lg transition hover:shadow-md">
          <label className="block text-gray-700 font-medium mb-2">
            {question.questionText}
            <span className="text-red-500 ml-1">*</span>
          </label>
          {question.questionType === 'text' && (
            <input
              type="text"
              maxLength={question.maxLength}
              value={answers[`question${index + 1}`] || ''}
              onChange={(e) => handleChange(`question${index + 1}`, e.target.value, question)}
              className="w-full border rounded px-3 py-2"
            />
          )}
          {question.questionType === 'checkbox' && (
            <div className="flex flex-col">
              {question.options.map((option, idx) => (
                <label key={idx} className="inline-flex items-center mt-1">
                  <input
                    type="checkbox"
                    value={option}
                    checked={answers[`question${index + 1}`]?.includes(option) || false}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const current = answers[`question${index + 1}`] || [];
                      if (checked) {
                        handleChange(`question${index + 1}`, [...current, option], question);
                      } else {
                        handleChange(`question${index + 1}`, current.filter(item => item !== option), question);
                      }
                    }}
                    className="form-checkbox"
                  />
                  <span className="ml-2">{option}</span>
                </label>
              ))}
            </div>
          )}
          {question.questionType === 'radio' && (
            <div className="flex flex-col">
              {question.options.map((option, idx) => (
                <label key={idx} className="inline-flex items-center mt-1">
                  <input
                    type="radio"
                    name={`question${index + 1}`}
                    value={option}
                    checked={answers[`question${index + 1}`] === option}
                    onChange={(e) => handleChange(`question${index + 1}`, e.target.value, question)}
                    className="form-radio"
                  />
                  <span className="ml-2">{option}</span>
                </label>
              ))}
            </div>
          )}
          {question.questionType === 'dropdown' && (
            <select
              value={answers[`question${index + 1}`] || ''}
              onChange={(e) => handleChange(`question${index + 1}`, e.target.value, question)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">Select an option</option>
              {question.options.map((option, idx) => (
                <option key={idx} value={option}>{option}</option>
              ))}
            </select>
          )}
          {question.questionType === 'textarea' && (
            <textarea
              maxLength={question.maxLength}
              value={answers[`question${index + 1}`] || ''}
              onChange={(e) => handleChange(`question${index + 1}`, e.target.value, question)}
              className="w-full border rounded px-3 py-2"
            />
          )}
          {question.questionType === 'number' && (
            <input
              type="number"
              min={question.minValue}
              max={question.maxValue}
              value={answers[`question${index + 1}`] || ''}
              onChange={(e) => handleChange(`question${index + 1}`, e.target.value, question)}
              className="w-full border rounded px-3 py-2"
            />
          )}
          {question.questionType === 'date' && (
            <input
              type="date"
              min={question.minDate}
              max={question.maxDate}
              value={answers[`question${index + 1}`] || ''}
              onChange={(e) => handleChange(`question${index + 1}`, e.target.value, question)}
              className="w-full border rounded px-3 py-2"
            />
          )}
          {errors[`question${index + 1}`] && (
            <p className="text-red-500 text-sm mt-1">{errors[`question${index + 1}`]}</p>
          )}
        </div>
      ))}
    </div>
  );
});

// Update PaymentSummary component with coupon validation functions
const PaymentSummary = forwardRef(({ 
  event, 
  answers, 
  handleSubmit, 
  isSubmitting, 
  userData, 
  appliedCoupon, 
  setAppliedCoupon,
  handlePrev
}, ref) => {
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  
  // Check if event is free
  const isFreeEvent = !event?.price || parseFloat(event.price) === 0;

  // Add coupon validation function
  const validateCoupon = () => {
    if (isValidatingCoupon) return;
    setIsValidatingCoupon(true);
    setCouponError('');

    try {
      // Validate coupon format
      if (!couponCode || couponCode.trim().length === 0) {
        setCouponError('Please enter a coupon code');
        return;
      }

      // Check if coupons exist for the event
      if (!event.coupons || !Array.isArray(event.coupons)) {
        setCouponError('No valid coupons available for this event');
        return;
      }

      // Find matching coupon
      const matchingCoupon = event.coupons.find(
        coupon => coupon.code.toUpperCase() === couponCode.toUpperCase()
      );

      if (!matchingCoupon) {
        setCouponError('Invalid coupon code');
        return;
      }

      // Check if coupon is expired
      if (matchingCoupon.expiryDate && new Date(matchingCoupon.expiryDate) < new Date()) {
        setCouponError('This coupon has expired');
        return;
      }

      // Check if coupon usage limit is exceeded
      if (matchingCoupon.maxUses && matchingCoupon.usedCount >= matchingCoupon.maxUses) {
        setCouponError('This coupon has reached its usage limit');
        return;
      }

      // Apply valid coupon
      setAppliedCoupon(matchingCoupon);
      setCouponCode('');
    } catch (error) {
      console.error('Coupon validation error:', error);
      setCouponError('Error validating coupon');
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  // Add remove coupon function
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  // Add function to handle coupon input
  const handleCouponInput = (e) => {
    const value = e.target.value.toUpperCase();
    setCouponCode(value);
    if (couponError) {
      setCouponError('');
    }
  };

  // Add keypress handler for coupon input
  const handleCouponKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      validateCoupon();
    }
  };

  // Existing price calculation for paid events
  const calculatePriceBreakdown = () => {
    // If free event, return zero values
    if (isFreeEvent) {
      return {
        baseAmount: 0,
        discount: 0,
        discountedAmount: 0,
        convenienceFee: 0,
        gst: 0,
        total: 0
      };
    }

    // Existing price calculation for paid events
    const baseAmount = parseFloat(event.price);
    const discount = appliedCoupon 
      ? (baseAmount * (parseInt(appliedCoupon.discount) / 100))
      : 0;
    const discountedAmount = baseAmount - discount;
    const convenienceFee = discountedAmount * 0.02;
    const gst = convenienceFee * 0.18;
    return {
      baseAmount,
      discount,
      discountedAmount,
      convenienceFee,
      gst,
      total: discountedAmount + convenienceFee + gst
    };
  };

  const priceBreakdown = calculatePriceBreakdown();

  return (
    <div className="animate-fadeIn">
      {/* Main Header with Event Context */}
      <div className="mb-8">
        <h3 className="text-2xl sm:text-3xl font-bold text-gray-800">
          Review & Payment
        </h3>
        <p className="mt-2 text-gray-600">
          {event.title}
        </p>
      </div>
      
      {/* Mobile Stepper - Shows current section */}
      <div className="flex items-center justify-between mb-6 md:hidden">
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <span className="w-6 h-6 flex items-center justify-center bg-indigo-600 text-white rounded-full">
            4
          </span>
          <span>Final Step</span>
        </div>
        <div className="text-sm text-gray-500">
          Review your details
        </div>
      </div>

      {/* Enhanced Responses Review Section - Mobile First */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100 mb-6 overflow-hidden">
        {/* Section Header */}
        <div className="px-4 sm:px-6 py-4 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold text-gray-800">
              Your Responses
            </h4>
            <button 
              onClick={() => handlePrev()} 
              className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
            >
              Edit Responses
            </button>
          </div>
        </div>

        {/* Responses List - Optimized for Mobile */}
        <div className="divide-y divide-gray-100">
          {event.questions.map((question, index) => (
            <div key={index} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
              {/* Question */}
              <div className="mb-2">
                <p className="text-sm sm:text-base text-gray-600 font-medium">
                  {question.questionText}
                </p>
              </div>

              {/* Answer with Enhanced Formatting */}
              <div className="ml-0 sm:ml-4">
                {(() => {
                  const answer = answers[`question${index + 1}`];
                  
                  // Handle Multiple Choice/Checkbox Responses
                  if (Array.isArray(answer)) {
                    return (
                      <div className="space-y-1">
                        {answer.map((item, i) => (
                          <div key={i} className="flex items-center space-x-2">
                            <span className="w-2 h-2 bg-indigo-600 rounded-full"></span>
                            <span className="text-gray-800">{item}</span>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  
                  // Handle Long Text Responses
                  if (typeof answer === 'string' && answer.length > 100) {
                    return (
                      <div className="mt-2 p-3 bg-gray-50 rounded-lg text-gray-800 text-sm sm:text-base whitespace-pre-wrap">
                        {answer}
                      </div>
                    );
                  }
                  
                  // Handle Short Text/Default Responses
                  return (
                    <p className="text-gray-800 text-sm sm:text-base">
                      {answer || <span className="text-gray-400 italic">No response provided</span>}
                    </p>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Coupon Section - Enhanced Mobile Experience */}
      {!isFreeEvent && (
        <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-4 sm:p-6 mb-6">
          <h4 className="text-lg font-semibold text-gray-800 mb-4">
            Apply Coupon
          </h4>
          
          {!appliedCoupon ? (
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={couponCode}
                  onChange={handleCouponInput}
                  onKeyPress={handleCouponKeyPress}
                  placeholder="Enter coupon code"
                  className={`
                    w-full px-4 py-3 rounded-lg border
                    text-gray-800 placeholder-gray-400
                    focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500
                    transition-all duration-200
                    ${couponError ? 'border-red-300' : 'border-gray-200'}
                  `}
                  disabled={isValidatingCoupon}
                />
                {isValidatingCoupon && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin h-5 w-5 border-2 border-indigo-600 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              <button
                onClick={validateCoupon}
                disabled={isValidatingCoupon || !couponCode}
                className={`
                  px-6 py-3 rounded-lg font-medium
                  transition-all duration-200
                  ${isValidatingCoupon || !couponCode
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700 active:scale-[0.98]'
                  }
                `}
              >
                Apply
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-100">
              <div className="flex items-center space-x-3">
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-green-100">
                  <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <span className="text-green-700">
                  Coupon <span className="font-medium">{appliedCoupon.code}</span> applied! ({appliedCoupon.discount}% off)
                </span>
              </div>
              <button
                onClick={removeCoupon}
                className="text-sm text-red-600 hover:text-red-700 font-medium"
              >
                Remove
              </button>
            </div>
          )}
          {couponError && (
            <div className="mt-2 text-sm text-red-600">
              {couponError}
            </div>
          )}
        </div>
      )}

      {/* Price Breakdown - Enhanced Mobile Design */}
      <div className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden mb-6">
        <div className="px-4 sm:px-6 py-4 bg-gray-50 border-b border-gray-100">
          <h4 className="text-lg font-semibold text-gray-800">
            {isFreeEvent ? 'Free Registration' : 'Price Details'}
          </h4>
        </div>
        
        <div className="p-4 sm:p-6">
          {isFreeEvent ? (
            <div className="flex items-center space-x-3 text-green-600">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="font-medium">This event is free to register!</span>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Base Price */}
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Base Price</span>
                <span className="text-gray-800 font-medium">₹{priceBreakdown.baseAmount.toFixed(2)}</span>
              </div>

              {/* Discount if applied */}
              {appliedCoupon && (
                <div className="flex justify-between items-center text-green-600">
                  <span>Discount ({appliedCoupon.discount}%)</span>
                  <span className="font-medium">-₹{priceBreakdown.discount.toFixed(2)}</span>
                </div>
              )}

              {/* Convenience Fee */}
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Convenience Fee (2%)</span>
                <span className="text-gray-800 font-medium">₹{priceBreakdown.convenienceFee.toFixed(2)}</span>
              </div>

              {/* GST */}
              <div className="flex justify-between items-center">
                <span className="text-gray-600">GST (18%)</span>
                <span className="text-gray-800 font-medium">₹{priceBreakdown.gst.toFixed(2)}</span>
              </div>

              {/* Total - Highlighted */}
              <div className="pt-3 mt-3 border-t border-gray-100">
                <div className="flex justify-between items-center">
                  <span className="text-gray-900 font-semibold">Total Amount</span>
                  <span className="text-lg text-gray-900 font-bold">₹{priceBreakdown.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Payment Button for Mobile */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 md:relative md:p-0 md:bg-transparent md:border-0">
        <button
          onClick={() => handleSubmit(appliedCoupon?.code, priceBreakdown.total)}
          disabled={isSubmitting}
          className={`
            w-full py-4 px-6 rounded-xl
            text-white text-lg font-medium
            transition-all duration-300
            ${isSubmitting 
              ? 'bg-indigo-500 cursor-wait'
              : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99]'
            }
            focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2
            shadow-lg shadow-indigo-600/20
          `}
          type="button"
        >
          <div className="relative flex items-center justify-center gap-3">
            {/* Payment Icon */}
            {!isSubmitting && (
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" 
                />
              </svg>
            )}

            {/* Button Text */}
            {isSubmitting ? (
              <div className="flex items-center gap-3">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" 
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Processing Payment...</span>
              </div>
            ) : (
              <span>{isFreeEvent ? 'Register Now' : `Pay ₹${priceBreakdown.total.toFixed(2)}`}</span>
            )}
          </div>
        </button>
      </div>

      {/* Bottom Padding for Mobile Fixed Button */}
      <div className="h-24 md:h-0" />
    </div>
  );
});

export default RegisterEvent;